import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, RefreshCw, Trash2, Smartphone, Search, X } from "lucide-react";

// Extrai modelo simplificado do userAgent (ex: "iPhone", "Android 13", "Chrome/Windows").
// Não perfeito, mas suficiente pra agrupar padrões de crash sem depender de lib externa.
const deviceModel = (ua) => {
  if (!ua) return "Desconhecido";
  if (/iPhone/i.test(ua)) return "iPhone";
  if (/iPad/i.test(ua)) return "iPad";
  if (/Android/i.test(ua)) {
    const m = ua.match(/Android\s+(\d+(?:\.\d+)?)/i);
    return m ? `Android ${m[1]}` : "Android";
  }
  if (/Windows/i.test(ua)) return "Windows";
  if (/Macintosh|Mac OS/i.test(ua)) return "Mac";
  if (/Linux/i.test(ua)) return "Linux";
  return "Outro";
};

// Painel SA-only: visualiza os últimos crashes do mobile enviados via ErrorBoundary
// + window.onerror + onunhandledrejection. Refresca a cada 15s (polling leve).
// Permite purgar todos os logs após triagem.
export default function MobileLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [purging, setPurging] = useState(false);
  const [search, setSearch] = useState("");
  const [filterDevice, setFilterDevice] = useState("all");
  const [filterVersion, setFilterVersion] = useState("all");
  const [filterKind, setFilterKind] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/mobile-logs?limit=300");
      setLogs(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar logs");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);   // polling 15s
    return () => clearInterval(t);
    // `load` é estável dentro do componente e só depende de setState (identidade da closure).
    // Executamos apenas no mount; unmount cancela o polling.
  }, []);

  const purge = async () => {
    if (!window.confirm(`Apagar TODOS os ${logs.length} log(s) de erro? Esta ação não pode ser desfeita.`)) return;
    setPurging(true);
    try {
      const r = await api.delete("/mobile-logs");
      toast.success(`${r.data.deleted} log(s) removido(s)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao limpar");
    } finally { setPurging(false); }
  };

  const kindColor = {
    error_boundary: "bg-red-100 text-red-800 border-red-300",
    window_error: "bg-orange-100 text-orange-800 border-orange-300",
    unhandled_rejection: "bg-amber-100 text-amber-800 border-amber-300",
  };
  const kindLabel = {
    error_boundary: "React crash",
    window_error: "JS error",
    unhandled_rejection: "Promise rejeitada",
  };

  // Opções únicas pros dropdowns — derivadas dos logs carregados
  const deviceOptions = useMemo(() => {
    const set = new Set(logs.map((l) => deviceModel(l.device)));
    return Array.from(set).sort();
  }, [logs]);
  const versionOptions = useMemo(() => {
    const set = new Set(logs.map((l) => l.app_version).filter(Boolean));
    return Array.from(set).sort().reverse();
  }, [logs]);

  // Filtragem client-side (rápido, evita round-trip)
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return logs.filter((l) => {
      if (filterDevice !== "all" && deviceModel(l.device) !== filterDevice) return false;
      if (filterVersion !== "all" && l.app_version !== filterVersion) return false;
      if (filterKind !== "all" && l.kind !== filterKind) return false;
      if (q) {
        const hay = `${l.message || ""} ${l.stack || ""} ${l.screen || ""} ${l.device || ""} ${l.user_email || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [logs, search, filterDevice, filterVersion, filterKind]);

  const hasActiveFilters = search || filterDevice !== "all" || filterVersion !== "all" || filterKind !== "all";
  const clearFilters = () => {
    setSearch(""); setFilterDevice("all"); setFilterVersion("all"); setFilterKind("all");
  };

  return (
    <div className="space-y-6" data-testid="page-mobile-logs">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-red-600 text-white"><AlertTriangle className="w-6 h-6" /></div>
        <div className="flex-1">
          <h1 className="text-3xl font-extrabold leading-tight">Logs de Erro Mobile</h1>
          <p className="text-sm text-muted-foreground">Crashes capturados no PWA/mobile — envie ao suporte pra investigação.</p>
        </div>
        <Button onClick={load} disabled={loading} variant="outline" data-testid="btn-refresh-logs">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />Atualizar
        </Button>
        {logs.length > 0 && (
          <Button onClick={purge} disabled={purging} variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="btn-purge-logs">
            <Trash2 className="w-4 h-4 mr-2" />Limpar tudo
          </Button>
        )}
      </div>

      {loading && logs.length === 0 && (
        <div className="text-center py-16 text-muted-foreground">Carregando…</div>
      )}

      {!loading && logs.length === 0 && (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground" data-testid="logs-empty">
            <Smartphone className="w-12 h-12 mx-auto mb-3 text-emerald-500" />
            <p className="font-semibold text-lg">Sem crashes reportados</p>
            <p className="text-xs mt-1">Ótimo! Nenhum erro do mobile foi capturado ainda.</p>
          </CardContent>
        </Card>
      )}

      {logs.length > 0 && (
        <Card>
          <CardContent className="pt-6 grid gap-3 md:grid-cols-4">
            <div className="md:col-span-2 relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar mensagem, stack, tela, usuário…" className="pl-9"
                data-testid="logs-search" />
            </div>
            <Select value={filterDevice} onValueChange={setFilterDevice}>
              <SelectTrigger data-testid="logs-filter-device"><SelectValue placeholder="Modelo" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os modelos</SelectItem>
                {deviceOptions.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterVersion} onValueChange={setFilterVersion}>
              <SelectTrigger data-testid="logs-filter-version"><SelectValue placeholder="Versão" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as versões</SelectItem>
                {versionOptions.map((v) => <SelectItem key={v} value={v}>v{v.replace(/^v/, "")}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="md:col-span-3 flex items-center gap-2 text-xs text-muted-foreground">
              <Select value={filterKind} onValueChange={setFilterKind}>
                <SelectTrigger className="w-auto h-8" data-testid="logs-filter-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os tipos</SelectItem>
                  <SelectItem value="error_boundary">React crash</SelectItem>
                  <SelectItem value="window_error">JS error</SelectItem>
                  <SelectItem value="unhandled_rejection">Promise rejeitada</SelectItem>
                </SelectContent>
              </Select>
              <span data-testid="logs-count-summary">
                Mostrando <b className="text-slate-800">{filtered.length}</b> de {logs.length} log(s)
              </span>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="logs-clear-filters" className="h-7 ml-auto">
                  <X className="w-3 h-3 mr-1" />Limpar filtros
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {logs.length > 0 && filtered.length === 0 && (
        <div className="text-center py-10 text-muted-foreground" data-testid="logs-no-match">
          <Search className="w-10 h-10 mx-auto mb-2 text-orange-400" />
          <p className="font-semibold">Nenhum log com esses filtros</p>
          <p className="text-xs mt-1">Ajuste os filtros acima ou limpe pra ver todos.</p>
        </div>
      )}

      <div className="space-y-3">
        {filtered.map((log) => (
          <Card key={log.id} className="border-l-4 border-l-red-500" data-testid={`log-${log.id}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                <Badge className={`text-[10px] uppercase ${kindColor[log.kind] || "bg-slate-100 text-slate-700"}`}>
                  {kindLabel[log.kind] || log.kind}
                </Badge>
                <span className="text-xs text-muted-foreground font-mono">{new Date(log.created_at).toLocaleString("pt-BR")}</span>
                {log.app_version && (
                  <Badge variant="outline" className="text-[10px]">v{log.app_version.replace(/^v/, "")}</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-2">
              <div className="text-sm font-semibold text-slate-800 break-words">{log.message}</div>
              {log.screen && (
                <div className="text-xs">
                  <span className="text-muted-foreground">Tela: </span>
                  <span className="font-mono text-slate-600">{log.screen}</span>
                </div>
              )}
              {log.user_email && (
                <div className="text-xs"><span className="text-muted-foreground">Usuário: </span><span className="font-mono">{log.user_email}</span></div>
              )}
              {log.device && (
                <div className="text-xs text-slate-500 break-all">
                  <span className="text-muted-foreground">Device: </span>
                  <Badge variant="outline" className="text-[10px] mr-1">{deviceModel(log.device)}</Badge>
                  {log.device}
                </div>
              )}
              {log.stack && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-slate-500 hover:text-slate-700">Stack trace</summary>
                  <pre className="mt-1 bg-slate-50 border border-slate-200 rounded p-2 overflow-x-auto text-[10px] leading-tight font-mono whitespace-pre-wrap break-all">{log.stack}</pre>
                </details>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
